import {
  Text,
  Group,
  Button,
  Paper,
  ActionIcon,
  Box,
  Loader,
  Tooltip,
  ThemeIcon,
} from '@mantine/core';
import {
  IconCheck,
  IconChevronRight,
  IconShoppingBag,
  IconSettings,
} from '@tabler/icons-react';
import { MealPlan, GroceryItem } from '../../types/mealPlan';
import { GroceryListProgress } from './GroceryListProgress';
import { SplitButton } from '../UI/SplitButton';
import recipeDetailsClasses from '../Recipe/RecipeDetailsView.module.css';
import classes from './ShoppingList.module.css';

type ShoppingListProps = {
  mealPlan: MealPlan;
  groceryList: GroceryItem[];
  addingToCart: boolean;
  cartProgress: {
    step: 'pending' | 'searching_products' | 'matching_products' | 'adding_to_cart' | 'completed' | 'failed';
    message: string;
    itemsProcessed?: number;
    totalItems?: number;
    matchingProcessed?: number;
    matchingTotal?: number;
  };
  cartResults: {
    success: boolean;
    message: string;
    addedItems?: Array<{
      itemName: string;
      productName: string;
      quantity: number;
      reasoning?: string;
      productPageURI?: string;
    }>;
    failedItems?: Array<{
      itemName: string;
      reason: string;
    }>;
  } | null;
  setCartModalOpen: (open: boolean) => void;
  initialCartCheckComplete: boolean;
  isOwner: boolean;
  isMobile: boolean;
  isKrogerConnected: () => boolean;
  handleAddToKrogerCart: () => void;
  handleAddToInstacart: () => void;
  addingToInstacart: boolean;
  setAdditionalItemsDrawerOpen: (open: boolean) => void;
  onOpenList: () => void;
  onRetryGeneration: () => void;
};

export const ShoppingList = ({
  mealPlan,
  groceryList,
  addingToCart,
  cartProgress,
  cartResults,
  setCartModalOpen,
  initialCartCheckComplete,
  isOwner,
  isMobile,
  isKrogerConnected,
  handleAddToKrogerCart,
  handleAddToInstacart,
  addingToInstacart,
  setAdditionalItemsDrawerOpen,
  onOpenList,
  onRetryGeneration,
}: ShoppingListProps) => {
  const generationStatus = mealPlan.groceryListStatus?.status;
  const listReady = generationStatus === 'completed' && groceryList.length > 0;
  const neededCount = groceryList.filter(item => !item.alreadyHave).length;
  const additionalCount = mealPlan?.additionalItems?.length || 0;
  const isCartDisabled = neededCount === 0;
  const cartBusy = addingToCart || addingToInstacart;

  // The single status slot under the title. Exactly one of these renders,
  // in priority order: generation state, cart-in-flight, cart results,
  // then the default counts + view-list hint.
  const renderStatus = () => {
    if (generationStatus === 'pending' || generationStatus === 'generating') {
      return (
        <GroceryListProgress
          status={generationStatus}
          progress={mealPlan.groceryListProgress}
        />
      );
    }

    if (generationStatus === 'failed') {
      // The task is dead at this point — refreshing the page can't revive
      // it, only a regenerate can. Surface the server's message and a real
      // retry.
      return (
        <Text size="xs" c="red" lh={1.3}>
          {mealPlan.groceryListStatus?.errorMessage ||
            "We couldn't finish your shopping list."}
          {isOwner && (
            <>
              {' '}
              <Text
                component="span"
                size="xs"
                fw={600}
                td="underline"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRetryGeneration();
                }}
              >
                Try again
              </Text>
            </>
          )}
        </Text>
      );
    }

    if (addingToCart) {
      return (
        <Group gap={6} wrap="nowrap">
          <Loader size="xs" />
          <Text size="xs" c="dimmed" lh={1.3}>
            {cartProgress.step === 'pending' && 'Getting started...'}
            {cartProgress.step === 'searching_products' &&
              `Finding your groceries... (${cartProgress.itemsProcessed || 0}/${cartProgress.totalItems || 0})`
            }
            {cartProgress.step === 'matching_products' &&
              `Picking the best options... (${cartProgress.matchingProcessed || 0}/${cartProgress.matchingTotal || 0})`
            }
            {cartProgress.step === 'adding_to_cart' && 'Adding to your cart...'}
          </Text>
        </Group>
      );
    }

    if (cartResults) {
      return (
        <Text
          size="xs"
          c={cartResults.success ? 'green.8' : 'red'}
          fw={500}
          lh={1.3}
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            setCartModalOpen(true);
          }}
        >
          {cartResults.success
            ? `${cartResults.addedItems?.length || 0} items added to cart ↗`
            : 'Cart update failed - View details ↗'
          }
        </Text>
      );
    }

    if (listReady) {
      const showInstacartLink = initialCartCheckComplete
        && !mealPlan?.cartAdditionStatus?.status
        && !!mealPlan?.instacartShoppingListCache?.url;

      return (
        <Text size="xs" c="dimmed" lh={1.3}>
          {neededCount} of {groceryList.length} items needed
          {additionalCount > 0 && ` · ${additionalCount} additional`}
          {showInstacartLink && (
            <>
              {' · '}
              <Text
                component="span"
                size="xs"
                c="teal.7"
                fw={500}
                style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(mealPlan.instacartShoppingListCache!.url, '_blank');
                }}
              >
                View in Instacart ↗
              </Text>
            </>
          )}
        </Text>
      );
    }

    if (generationStatus === 'completed') {
      return (
        <Text size="xs" c="dimmed" lh={1.3}>
          No items in your list
        </Text>
      );
    }

    return null;
  };

  return (
    <>
      {/* Shopping list */}
      {mealPlan.status === 'active' && (
        <Paper
          withBorder
          radius="lg"
          p="md"
          data-tour="shopping-section"
          className={listReady ? classes.card : undefined}
          onClick={listReady ? onOpenList : undefined}
        >
          <Group justify="space-between" align="center" wrap="wrap" gap="sm">
            {/* Title + status */}
            <Group gap="sm" wrap="nowrap" align="center" style={{ flex: 1, minWidth: 0 }} data-tour="grocery-list-intro">
              <ThemeIcon variant="light" color="orange" size="lg" radius="md">
                <IconShoppingBag size={18} />
              </ThemeIcon>
              {/* flex: 1, not shrink-to-fit: the generation state renders a
                  progress bar sized by this box, and content-driven width
                  would make the bar resize as the status text changes. */}
              <Box style={{ minWidth: 0, flex: 1 }}>
                <Group gap={2} wrap="nowrap">
                  <Text fw={600} lh={1.3}>Shopping list</Text>
                  {listReady && (
                    <IconChevronRight size={16} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0 }} />
                  )}
                </Group>
                {renderStatus()}
              </Box>
            </Group>

            {/* Actions */}
            {listReady && isOwner && (
              <Group
                gap="xs"
                justify={isMobile ? "center" : "flex-end"}
                wrap="nowrap"
                w={{ base: '100%', sm: 'auto' }}
                onClick={(e) => e.stopPropagation()}
              >
                {mealPlan?.cartAdditionStatus?.status === 'completed' ? (
                  // Show success state after successful cart addition
                  <Group
                    gap="sm"
                    align="flex-start"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setCartModalOpen(true)}
                  >
                    <IconCheck size={16} color="green" style={{ marginTop: '1px' }} />
                    <Box>
                      <Text size="xs" c="green.8" fw={500}>
                        Added to Kroger cart{' '}↗
                      </Text>
                      {mealPlan?.cartAdditionStatus?.completedAt && (
                        <Text size="xs" c="dimmed">
                          {new Date(mealPlan.cartAdditionStatus.completedAt).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </Text>
                      )}
                    </Box>
                  </Group>
                ) : (
                  <>
                    <Tooltip label="Add groceries & customize preferences" position="top" withArrow>
                      <ActionIcon
                        onClick={() => setAdditionalItemsDrawerOpen(true)}
                        variant="subtle"
                        color="gray"
                        size="lg"
                        style={{ flexShrink: 0 }}
                        disabled={isCartDisabled || cartBusy}
                        data-tour="add-item-button"
                      >
                        <IconSettings size={20} />
                      </ActionIcon>
                    </Tooltip>
                    {isKrogerConnected() ? (
                      <SplitButton
                        mainAction={{
                          label: (
                            <Group gap={6} align="center" wrap="nowrap">
                              <Text size="sm">Add to</Text>
                              <img
                                src="/kroger-logo.svg"
                                alt="Kroger"
                                style={{
                                  height: '18px',
                                  width: 'auto',
                                  filter: (isCartDisabled || cartBusy) ? 'grayscale(1) opacity(0.6)' : 'brightness(0) invert(1)'
                                }}
                              />
                              <Text size="sm">Cart</Text>
                            </Group>
                          ) as any,
                          onClick: handleAddToKrogerCart,
                        }}
                        data-tour="add-to-kroger-cart"
                        menuItems={[
                          {
                            label: (
                              <Group gap={6} align="center">
                                <Text>Order with</Text>
                                <img
                                  src="/images/instacart_logo.png"
                                  alt="Instacart"
                                  style={{
                                    height: '14px',
                                    width: 'auto'
                                  }}
                                />
                              </Group>
                            ) as any,
                            onClick: handleAddToInstacart,
                          },
                        ]}
                        disabled={isCartDisabled || cartBusy}
                        loading={cartBusy}
                        style={{
                          flex: 1,
                          backgroundColor: (isCartDisabled || cartBusy) ? '#e9ecef' : '#134B97',
                          color: (isCartDisabled || cartBusy) ? '#6c757d' : 'white',
                        }}
                      />
                    ) : (
                      <Button
                        onClick={handleAddToInstacart}
                        className={recipeDetailsClasses.instacartButton}
                        variant="filled"
                        loading={addingToInstacart}
                        disabled={isCartDisabled || addingToInstacart}
                        loaderProps={{
                          color: (isCartDisabled || addingToInstacart) ? 'dimmed' : 'white',
                          size: 'sm'
                        }}
                        style={{
                          backgroundColor: (isCartDisabled || addingToInstacart) ? '#e9ecef' : '#003e2a',
                          color: (isCartDisabled || addingToInstacart) ? '#6c757d' : '#FAF1E5',
                          borderRadius: '28px'
                        }}
                        data-tour="create-instacart-list"
                      >
                        <Group gap={6} align="center" wrap="nowrap">
                          <Text size="sm">Order with</Text>
                          <img
                            src="/images/instacart_logo_cashew.png"
                            alt="Instacart"
                            style={{
                              height: '14px',
                              width: 'auto',
                              filter: (isCartDisabled || addingToInstacart) ? 'grayscale(1) brightness(0.6)' : 'none'
                            }}
                          />
                        </Group>
                      </Button>
                    )}
                  </>
                )}
              </Group>
            )}
          </Group>
        </Paper>
      )}
    </>
  );
};
